/**
 * Utilidades compartidas por las pantallas de **Pricing**.
 *
 * La vista de *Control de Precios* se reparte en varias secciones (cambios,
 * precios, competencia y análisis) que comparten los mismos filtros y los mismos
 * cálculos, así que las piezas comunes viven aquí.
 */

import { ApiError } from '@/lib/http/api-error';

/** Estilo de los campos de formulario del panel. */
export const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Mensaje legible de un error (de la API o de red). */
export function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Fecha de hoy en `AAAA-MM-DD`. */
export function hoy(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Fecha y hora legibles de una marca ISO o de una fecha del servicio. */
export function fechaHora(valor?: string | null): string {
  if (!valor) return '—';

  const fecha = new Date(valor);

  if (Number.isNaN(fecha.getTime())) return valor;

  return fecha.toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' });
}

/** Separa `DEPARTAMENTO_PROVINCIA_DISTRITO` en sus tres partes. */
export function partesDeUbicacion(
  ubicacion: string,
): { departamento: string; provincia: string; distrito: string } | null {
  const [departamento, provincia, distrito] = String(ubicacion || '').split('_');

  if (!departamento || !provincia || !distrito) return null;

  return { departamento, provincia, distrito };
}

/** Texto comparable: sin espacios de sobra y en mayúsculas. */
export function normalizar(valor: unknown): string {
  return String(valor ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase();
}

/** Clave de seguimiento de una estación y un producto. */
export function claveCompetidor(estacion: string, producto: string): string {
  return `${normalizar(estacion)}|${normalizar(producto)}`;
}

/** ¿El texto buscado está en alguno de los campos? */
export function coincideBusqueda(busqueda: string, ...campos: (string | undefined)[]): boolean {
  const termino = normalizar(busqueda);

  if (!termino) return true;

  return campos.some((campo) => normalizar(campo).includes(termino));
}

/** Resumen (promedio, mínimo y máximo) de una lista de precios. */
export function resumenDePrecios(precios: number[]): {
  cantidad: number;
  promedio: number;
  minimo: number;
  maximo: number;
} {
  const validos = precios.filter((precio) => Number.isFinite(precio));

  if (validos.length === 0) return { cantidad: 0, promedio: 0, minimo: 0, maximo: 0 };

  const suma = validos.reduce((total, precio) => total + precio, 0);

  return {
    cantidad: validos.length,
    promedio: suma / validos.length,
    minimo: Math.min(...validos),
    maximo: Math.max(...validos),
  };
}

/**
 * Reparte una lista de precios en rangos y cuenta cuántos caen en cada uno.
 *
 * Se usa para el histograma del análisis: los rangos se calculan a partir del
 * mínimo y el máximo reales, redondeados hacia fuera para que todos entren.
 */
export function histograma(
  precios: number[],
  pasos = 6,
): { etiquetas: string[]; valores: number[] } {
  const validos = precios.filter((precio) => Number.isFinite(precio) && precio > 0);

  if (validos.length === 0) return { etiquetas: [], valores: [] };

  const minimo = Math.floor(Math.min(...validos));
  const maximo = Math.ceil(Math.max(...validos));
  const ancho = Math.max(1, Math.ceil((maximo - minimo) / pasos));
  const etiquetas: string[] = [];
  const valores: number[] = [];

  for (let inicio = minimo; inicio < maximo; inicio += ancho) {
    const fin = inicio + ancho;

    etiquetas.push(`${inicio}–${fin}`);
    valores.push(validos.filter((precio) => precio >= inicio && precio < fin).length);
  }

  return { etiquetas, valores };
}

/** Ordena de mayor a menor y deja sólo los primeros. */
export function topPor<T>(lista: T[], cantidad: number, valor: (item: T) => number): T[] {
  return [...lista].sort((a, b) => valor(b) - valor(a)).slice(0, cantidad);
}
